import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiList, FiArrowRight, FiCheckSquare, FiPlus, FiShare2,
  FiStar, FiCalendar
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const PersonalTodos = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/personal/todos" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/personal" className="hover:text-blue-600">Personal Workspace</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Todos</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-orange-100 dark:bg-orange-900/30 rounded-xl mb-4">
              <FiList className="w-6 h-6 text-orange-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Todos</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Quick personal todo lists and shared todos for lightweight task tracking.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Simple Yet Powerful</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Todos are perfect for quick task capture without the overhead of full project management. 
                Create personal lists or share todos with your team.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-full text-sm text-gray-700 dark:text-gray-200">✓ Quick capture</span>
                <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-full text-sm text-gray-700 dark:text-gray-200">✓ Personal lists</span>
                <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-full text-sm text-gray-700 dark:text-gray-200">✓ Shared todos</span>
                <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-full text-sm text-gray-700 dark:text-gray-200">✓ Due dates</span>
                <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-full text-sm text-gray-700 dark:text-gray-200">✓ Priorities</span>
              </div>
            </div>
          </section>

          {/* Creating Todos */}
          <section className="mb-16" id="create-todo">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Creating Todos</h2>
            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiPlus className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Quick Add</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Use the quick add bar to rapidly create todos. Just type and press Enter.
                </p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiStar className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Set Priority</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Mark important todos with priority levels to focus on what matters most.
                </p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiCalendar className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Add Due Date</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Set deadlines to stay on track. Get reminders as due dates approach.
                </p>
              </div>
            </div>
          </section>

          {/* Todo Lists */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Organizing with Lists</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Organize your todos into lists for better management:
              </p>
              <ul className="space-y-2">
                <li className="flex items-center space-x-2">
                  <FiCheckSquare className="w-4 h-4 text-green-500" />
                  <span className="text-gray-700 dark:text-gray-200">Create custom lists (Work, Personal, Shopping, etc.)</span>
                </li>
                <li className="flex items-center space-x-2">
                  <FiCheckSquare className="w-4 h-4 text-green-500" />
                  <span className="text-gray-700 dark:text-gray-200">Move todos between lists</span>
                </li>
                <li className="flex items-center space-x-2">
                  <FiCheckSquare className="w-4 h-4 text-green-500" />
                  <span className="text-gray-700 dark:text-gray-200">Archive completed lists</span>
                </li>
                <li className="flex items-center space-x-2">
                  <FiCheckSquare className="w-4 h-4 text-green-500" />
                  <span className="text-gray-700 dark:text-gray-200">Color-code lists for easy identification</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Shared Todos */}
          <section className="mb-16" id="shared-todos">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Shared Todos</h2>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <FiShare2 className="w-6 h-6 text-purple-600" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Collaborate with Others</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Share todo lists with team members for collaborative task tracking:
              </p>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li>• Share lists via email invitation</li>
                <li>• See who completed which todos</li>
                <li>• Real-time sync across all members</li>
                <li>• Comment on individual todos</li>
              </ul>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/personal/tasks"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Task Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/personal/projects"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Project Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default PersonalTodos;
